/**
 * TagAssignPopover — checkbox list of registry tags for one clinic.
 * Each toggle saves the full tagIds array (optimistic in nivApi).
 */

import { useRef, useState } from "react";
import { Check, GearSix, MagnifyingGlass } from "@phosphor-icons/react";
import { useToast } from "../../../../contexts/ToastContext";
import { useSetNivClinicTagIdsMutation } from "../../../../store/services/nivApi";
import useOutsideClose from "../shared/hooks/useOutsideClose";
import { UserTagDot } from "../shared/ClinicUserTags";

function TagAssignPopover({ clinicId, tagIds = [], tags = [], onClose, onManage, onAssigned, align = "start" }) {
  const ref = useRef(null);
  const { showError } = useToast();
  const [term, setTerm] = useState("");
  const [setTagIds] = useSetNivClinicTagIdsMutation();

  useOutsideClose(ref, onClose);

  const visible = term.trim()
    ? tags.filter((t) => t.label.toLowerCase().includes(term.trim().toLowerCase()))
    : tags;
  const selected = new Set(tagIds);

  const toggle = async (tagId) => {
    const next = selected.has(tagId)
      ? tagIds.filter((id) => id !== tagId)
      : [...tagIds, tagId];
    if (!selected.has(tagId) && onAssigned) onAssigned(tagId);
    try {
      await setTagIds({ clinicId, tagIds: next }).unwrap();
    } catch (error) {
      console.error("[CK-7305] Error assigning clinic tag:", {
        errorCode: "CK-7305",
        errorMessage: error?.message,
        clinicId,
        tagId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון התגיות");
    }
  };

  return (
    <div
      ref={ref}
      className={`nivck2-pop nivck2-pop-${align} nivck2-tag-pop`}
      role="dialog"
      aria-label="תגיות לקליניקה"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {tags.length > 5 && (
        <div className="nivck2-pop-search">
          <MagnifyingGlass size={13} />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="חיפוש תגית…"
            autoFocus
          />
        </div>
      )}
      <div className="nivck2-tag-pop-list">
        {visible.length === 0 && (
          <div className="nivck2-pop-empty">
            {tags.length === 0 ? "אין תגיות עדיין" : "לא נמצאו תגיות"}
          </div>
        )}
        {visible.map((tag) => {
          const on = selected.has(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              className={`nivck2-tag-opt${on ? " is-on" : ""}`}
              onClick={() => toggle(tag.id)}
            >
              <UserTagDot color={tag.color} size={9} />
              <span className="nivck2-tag-opt-label">{tag.label}</span>
              {on && <Check size={13} weight="bold" />}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="nivck2-pop-link"
        onClick={() => {
          onClose();
          onManage();
        }}
      >
        <GearSix size={13} />
        ניהול תגיות
      </button>
    </div>
  );
}

export default TagAssignPopover;
